import { useState, useEffect } from "react";
import axios from "axios";
import MakeGetRequest from "./utils/MakeGetRequest.jsx";
import makePostRequest from "./utils/makePostRequest";
import TitleTextSection from "./TitelTextSection";

const PollSection = () => {
  const {
    data: pollData,
    loading,
    error,
  } = MakeGetRequest({
    route: `polls/frontpage`,
  });

  const [resultsData, setResultsData] = useState(null);
  const [voting, setVoting] = useState(false);
  const [voteError, setVoteError] = useState("");
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [showResultsManually, setShowResultsManually] = useState(false);

  useEffect(() => {
    // Ladda resultat om användaren redan har röstat
    if (pollData?.user_has_voted) {
      fetchResults();
    }
  }, [pollData]);

  const fetchResults = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL;
      const response = await axios.get(`${apiUrl}polls/frontpage/results`, {
        withCredentials: true, // Följer appens standarder för auth
      });
      setResultsData(response.data);
    } catch (err) {
      console.error("Failed to fetch poll results", err);
    }
  };

  const handleVoteClick = (optionId) => {
    if (pollData.multiple_choices) {
      setSelectedOptions((prev) =>
        prev.includes(optionId)
          ? prev.filter((id) => id !== optionId)
          : [...prev, optionId],
      );
    } else {
      setSelectedOptions([optionId]);
    }
  };

  const submitVote = async (optionIds) => {
    if (!optionIds || optionIds.length === 0) return;
    setVoting(true);
    setVoteError("");

    try {
      // Skicka vote till backend. Om det är multiple_choices skickar vi för varje (eller behöver backend-stöd, men vi testar en i taget eller om api:et stödjer comma-separerad).
      // Om backend inte stödjer lista, kör vi Promise.all för varje ID
      await Promise.all(
        optionIds.map((id) =>
          makePostRequest(`polls/${pollData.id}/vote?option_id=${id}`),
        ),
      ).then((responses) => {
        const errorRes = responses.find((r) => r && r.error);
        if (errorRes) {
          throw errorRes;
        }
      });

      fetchResults();
    } catch (err) {
      let errMsg = err.error?.message || err.error || "Ett fel uppstod";
      if (
        typeof errMsg === "string" &&
        (errMsg.toLowerCase() === "okänt fel" ||
          errMsg.toLowerCase() === "unauthorized")
      ) {
        errMsg = "Logga in för att rösta";
      }
      setVoteError(errMsg || "Kunde inte registrera din röst. Är du inloggad?");
    } finally {
      setVoting(false);
      setSelectedOptions([]);
    }
  };

  const toggleShowResults = async () => {
    if (!showResultsManually && !resultsData) {
      await fetchResults();
    }
    setShowResultsManually((prev) => !prev);
  };

  if (loading) return null;
  // Döljer sig automatiskt ifall ingen omröstning är aktiv (backend returnerar 404 eller ingen data)
  if (error || !pollData) return null;

  const isShowingResults = pollData.user_has_voted || (showResultsManually && resultsData);
  const displayData = (isShowingResults && resultsData) ? resultsData : pollData;

  return (
    <div className="px-4 max-w-7xl m-auto my-6">
      <div className="sm:py-4 flex justify-between items-center">
        <TitleTextSection
          title="Omröstning"
          subtitle="Tyck till och gör din röst hörd"
        />
      </div>
      <div className="bg-[#131313] rounded-2xl p-6 border border-white/10 shadow-lg">
        <h2 className="text-2xl font-extrabold text-white uppercase tracking-wide mb-2">{displayData.title}</h2>
        {displayData.description && (
          <p className="text-sm text-stone-400 mb-6 leading-relaxed">{displayData.description}</p>
        )}

        {voteError && (
          <p className="text-error font-semibold mb-4">{voteError}</p>
        )}

        <div className="flex flex-col gap-3">
          {displayData.options.map((option) => {
            if (isShowingResults) {
              const percentage =
                displayData.total_votes > 0
                  ? Math.round(
                      (option.votes_count / displayData.total_votes) * 100,
                    )
                  : 0;

              // Backend might return an array if multiple votes were cast, otherwise standard id
              const isUserVote = Array.isArray(displayData.user_voted_for)
                ? displayData.user_voted_for.includes(option.id)
                : displayData.user_voted_for === option.id;

              return (
                <div
                  key={option.id}
                  className="relative w-full bg-[#181818] rounded-full h-12 overflow-hidden flex items-center px-6 border border-white/5"
                >
                  <div
                    className={`absolute left-0 top-0 h-full ${
                      isUserVote ? "bg-[#0047ff]" : "bg-neutral"
                    } opacity-20 transition-all duration-500`}
                    style={{ width: `${percentage}%` }}
                  ></div>
                  <div className="relative z-10 flex justify-between w-full font-semibold items-center text-sm">
                    <span className="truncate pr-2 text-stone-200">
                      {option.text}{" "}
                      {isUserVote && (
                        <span className="text-xs ml-2 text-[#0047ff] font-bold">
                          (Din röst)
                        </span>
                      )}
                    </span>
                    <span className="pl-2 text-white">{percentage}%</span>
                  </div>
                </div>
              );
            }

            return (
              <button
                key={option.id}
                onClick={() => handleVoteClick(option.id)}
                disabled={voting}
                className={`btn btn-block justify-start border font-semibold px-6 rounded-full transition-all duration-300 hover:scale-[1.01] ${
                  selectedOptions.includes(option.id)
                    ? "bg-[#0047ff] text-white border-none shadow-[0_4px_15px_rgba(0,71,255,0.25)]"
                    : "border-white/10 bg-transparent text-stone-300 hover:bg-white/5"
                }`}
              >
                {option.text}
              </button>
            );
          })}
        </div>

        {!isShowingResults && (
          <button
            onClick={() => submitVote(selectedOptions)}
            disabled={voting || selectedOptions.length === 0}
            className="btn bg-[#0047ff] hover:bg-blue-700 text-white border-none rounded-full w-full font-bold uppercase tracking-wider text-xs py-3.5 mt-6 transition-transform duration-300 hover:scale-[1.02] shadow-[0_4px_15px_rgba(0,71,255,0.3)]"
          >
            Rösta
          </button>
        )}

        {!pollData.user_has_voted && (
          <button
            onClick={toggleShowResults}
            className="btn btn-ghost btn-sm mt-3 w-full text-xs text-stone-400 hover:text-white uppercase font-bold tracking-wider"
          >
            {showResultsManually ? "Dölj röster" : "Visa röster"}
          </button>
        )}

        {isShowingResults && (
          <p className="mt-4 text-xs text-stone-400 text-right">
            Totalt antal röster: {displayData.total_votes || 0}
          </p>
        )}
      </div>
    </div>
  );
};

export default PollSection;
